/**
 * UpgradeGate - Componente de bloqueio com prompt de upgrade
 * Exibe overlay quando recurso não disponível no plano
 * Mostra info de trial (dias restantes, expiração)
 */
import React, { useEffect } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Lock, Clock, AlertTriangle } from "lucide-react";
import { useTranslation } from "../translations";
import { track } from "../lib/tracker";

const UpgradeGate = ({ feature, planName, trialExpired, daysRemaining, isTrial, alertLevel, onUpgrade, children, className = "" }) => {
  const { t } = useTranslation();

  useEffect(() => { track(feature, "blocked"); }, [feature]);

  return (
    <div className={`relative ${className}`} data-testid={`gate-${feature}`}>
      {/* Blurred content preview */}
      <div className="pointer-events-none select-none opacity-30 blur-[2px]">
        {children}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <Card className="max-w-md w-full mx-4 p-6 text-center shadow-xl border-2 border-amber-200 bg-white/95 backdrop-blur-sm">
          {trialExpired ? (
            <>
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-7 h-7 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-testid={`gate-title-${feature}`}>
                {t('gateTrialExpired') || 'Período de Teste Expirado'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('gateTrialExpiredMsg') || 'Seu período de teste terminou. Assine um plano para continuar usando este recurso.'}
              </p>
            </>
          ) : (
            <>
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-testid={`gate-title-${feature}`}>
                {t('gateTitle')}
              </h3>
              <p className="text-sm text-gray-600 mb-1">
                {t('gateCurrentPlan')}: <strong>{planName}</strong>
              </p>
              <p className="text-sm text-gray-500 mb-4">
                {t('gateUpgradeMsg')}
              </p>
            </>
          )}
          <Button 
            onClick={onUpgrade}
            className={`w-full ${trialExpired ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'} text-white`}
            data-testid={`gate-upgrade-${feature}`}
          >
            {t('gateUpgradeBtn')}
          </Button>
        </Card>
      </div>
    </div>
  );
};

/**
 * TrialBanner - Banner de alerta de trial dentro de funcionalidades acessíveis
 * Mostra contagem de dias restantes do trial
 */
export const TrialBanner = ({ daysRemaining, alertLevel, planName, onUpgrade }) => {
  const { t } = useTranslation();
  
  if (!alertLevel || daysRemaining < 0) return null;

  const styles = {
    info: "bg-blue-50 border-blue-200 text-blue-800",
    warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
    urgent: "bg-orange-50 border-orange-200 text-orange-800",
    critical: "bg-red-50 border-red-200 text-red-800",
  };

  const icons = {
    info: <Clock className="w-4 h-4" />,
    warning: <Clock className="w-4 h-4" />,
    urgent: <AlertTriangle className="w-4 h-4" />,
    critical: <AlertTriangle className="w-4 h-4" />,
  };

  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border mb-4 ${styles[alertLevel] || styles.info}`} data-testid="trial-banner">
      <div className="flex items-center gap-2">
        {icons[alertLevel] || icons.info}
        <span className="text-sm font-medium">
          {t('trialDaysLeft') || 'Período de teste'}: {daysRemaining} {daysRemaining === 1 ? (t('day') || 'dia') : (t('days') || 'dias')}
        </span>
      </div>
      <Button size="sm" variant="outline" onClick={onUpgrade} className="text-xs" data-testid="trial-banner-upgrade">
        {t('gateUpgradeBtn')}
      </Button>
    </div>
  );
};

export default UpgradeGate;
